import { useLocalStorage } from '@uidotdev/usehooks'
import { useQueryState } from 'nuqs'
import { useCallback, useState } from 'react'
import { AddFindingsDrawer } from '../../components/drawers/add-findings-drawer.tsx'
import { RepositorySettingsDrawer } from '../../components/drawers/repository-settings-drawer'

import { WorkflowDrawer, type DrawerContent } from '../../components/drawers/workflow-drawer'
import { OnboardingDialog } from '../../components/onboarding-dialog.tsx'
import { RepositoriesTable } from '../../components/tables/repositories-table.tsx'
import { usePagination } from '../../components/tables/use-pagination.ts'
import { useShowMutationError } from '../../hooks/use-show-mutation-error.tsx'
import { useToastService } from '../../services/toast-service.tsx'
import {
  useGetRepositories,
  useGetRepository,
  usePostUploadScan,
  usePrefetchIntegrations,
} from '../../utils/api-client/api-hooks.ts'
import type { Tool } from '../../utils/api-client/api-schemas.ts'
import { getEnvironmentData } from '../../utils/hooks/get-environment-data.ts'
import { getToolName } from '../../utils/repository-utils.tsx'
import { AddRepositoryModal } from '../repositories-page/add-repository-modal.tsx'
import * as styles from './repositories-page.css.ts'

export function RepositoriesPage() {
  // Warm the integrations cache so AddRepositoryModal doesn't show a loading state
  // when opened. Prefetch populates the cache without subscribing to the query.
  usePrefetchIntegrations()

  const { pagination, onPaginationChange } = usePagination({
    defaultPageIndex: 0,
    defaultPageSize: 50,
  })
  const [searchTerm, setSearchTerm] = useQueryState('search', {
    defaultValue: '',
    history: 'push',
  })

  const { data: repositories } = useGetRepositories({
    pageIndex: pagination.pageIndex,
    pageSize: pagination.pageSize,
    searchTerm,
  })

  const environmentData = getEnvironmentData()

  const [selectedWorkflowRepositoryId, setSelectedWorkflowRepositoryId] = useState<string>()

  const [selectedAddFindingsRepositoryId, setSelectedAddFindingsRepositoryId] = useState<string>()

  const [isOnboardingDialogOpen, setIsOnboardingDialogOpen] = useLocalStorage('isOnboardingDialogOpen', true)

  const [addRepositoryDialogOpen, setAddRepositoryDialogOpen] = useState(false)
  const handleOpenAddRepository = useCallback(() => setAddRepositoryDialogOpen(true), [])
  const handleCloseAddRepository = useCallback(() => setAddRepositoryDialogOpen(false), [])
  const handleAddFindings = useCallback((repositoryId: string) => setSelectedAddFindingsRepositoryId(repositoryId), [])

  const handleSyncTool = useHandleSyncTool()

  const [workflowDrawerContent, setWorkflowDrawerContent] = useState<DrawerContent>('create')

  const filterRepositories = useCallback(
    (value: string) => {
      // Only reset pagination if search term actually changed
      if (value !== searchTerm) {
        onPaginationChange({ pageIndex: 0, pageSize: pagination.pageSize })
      }
      setSearchTerm(value)
    },
    [searchTerm, setSearchTerm, onPaginationChange, pagination.pageSize]
  )

  const [selectedSettingsRepositoryId, setSelectedSettingsRepositoryId] = useState<string | undefined>()

  const { data: selectedSettingsRepository } = useGetRepository({ repositoryId: selectedSettingsRepositoryId })

  return (
    <div className={styles.mainContainer}>
      <div className={styles.topLevelContainer}>
        <OnboardingDialog isOpen={isOnboardingDialogOpen} setIsOpen={setIsOnboardingDialogOpen} />
        {selectedAddFindingsRepositoryId && (
          <AddFindingsDrawer
            repositoryId={selectedAddFindingsRepositoryId}
            onCloseDrawer={() => setSelectedAddFindingsRepositoryId(undefined)}
          />
        )}
        {selectedWorkflowRepositoryId && (
          <WorkflowDrawer
            repositoryId={selectedWorkflowRepositoryId}
            repositoryDisplayName={
              repositories?._embedded.items.find(repo => repo.id === selectedWorkflowRepositoryId)?.full_name ?? ''
            }
            repositoryType={
              repositories?._embedded.items.find(repo => repo.id === selectedWorkflowRepositoryId)?.type ?? 'git'
            }
            onCloseDrawer={() => {
              setSelectedWorkflowRepositoryId(undefined)
              setWorkflowDrawerContent('create')
            }}
            drawerContent={workflowDrawerContent}
            onSetDrawerContent={setWorkflowDrawerContent}
          />
        )}
        {addRepositoryDialogOpen && <AddRepositoryModal onClose={handleCloseAddRepository} />}
        {selectedSettingsRepository && (
          <RepositorySettingsDrawer
            repository={selectedSettingsRepository}
            onCloseDrawer={() => setSelectedSettingsRepositoryId(undefined)}
            onUploadClick={() => setSelectedAddFindingsRepositoryId(selectedSettingsRepository.id)}
          />
        )}

        <RepositoriesTable
          columnDefinitionOptions={{
            setSelectedWorkflowRepositoryId,
            setSelectedSettingsRepositoryId,
            setWorkflowDrawerContent,
            onAddFindings: handleAddFindings,
            onSyncTool: handleSyncTool,
          }}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          environmentData={environmentData}
          onOpenAddRepository={handleOpenAddRepository}
          searchTerm={searchTerm}
          onChangeSearchTerm={filterRepositories}
        />
      </div>
    </div>
  )
}

const useHandleSyncTool = () => {
  const { addToast } = useToastService()
  const showSyncError = useShowMutationError<typeof usePostUploadScan>({
    prefix: (_error, variables) => `Failed to sync ${getToolName(variables.body.metadata.tool)} findings`,
  })
  const { mutate } = usePostUploadScan()

  return useCallback(
    (repositoryId: string, tool: Tool, repositoryName: string) => {
      mutate(
        { repositoryId, body: { metadata: { tool, workflow_execution_policy: 'execute' } } },
        {
          onSuccess: () => {
            addToast({
              message: (
                <>
                  Latest {getToolName(tool)} findings synced to {repositoryName}
                </>
              ),
              variant: 'success',
            })
          },
          onError: showSyncError,
        }
      )
    },
    [mutate, addToast, showSyncError]
  )
}
